import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import CustomDatePicker from '../../components/CustomDatePicker';
import { getSubDCAByDCA } from '../../api/SpPOAPI/sppoInvoiceAPI';
import { S3_BASE_URL, UPLOAD_DOCS_PATH, S3_FOLDERS } from '../../config/s3Config';

import {
    fetchServiceVendors,
    fetchPOsForVendor, fetchPODetails,
    fetchVendorGSTNos, fetchCompanyGSTNos,
    fetchGSTConfig, fetchOtherChargeDCAs, fetchDeductionDCAs,
    checkBudget, submitSPPOInvoice,
    clearPODetails, clearGSTConfig, clearSaveResult, clearPOList,
    resetSppoInvoice,
    selectSPPOInvServiceVendors, selectSPPOInvServiceVendorsLoading,
    selectSPPOInvPOList, selectSPPOInvPODetails,
    selectSPPOInvVendorGSTNos, selectSPPOInvCompanyGSTNos,
    selectSPPOInvGSTConfig, selectSPPOInvOtherDCAs, selectSPPOInvDeductionDCAs,
    selectSPPOInvBudgetResult, selectSPPOInvSaveResult,
    selectSPPOInvPOListLoading, selectSPPOInvPODetailsLoading,
    selectSPPOInvVendorGSTLoading, selectSPPOInvCompanyGSTLoading,
    selectSPPOInvGSTConfigLoading, selectSPPOInvOtherDCAsLoading,
    selectSPPOInvDeductionDCAsLoading, selectSPPOInvBudgetLoading,
    selectSPPOInvSaveLoading, selectSPPOInvSaveError,
} from '../../slices/spPOSlice/sppoInvoiceSlice';

import {
    Receipt, Search, Plus, Trash2, RotateCcw, Send,
    Loader2, ChevronDown, IndianRupee, ShieldCheck,
    FileText, AlertCircle, CheckCircle2, Layers, Calculator, MapPin,
    X, Paperclip, Upload, CloudUpload,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [yyyy, mm, dd] = val.split('T')[0].split('-');
        return `${dd}-${MONTH_ABBR[parseInt(mm, 10) - 1]}-${yyyy}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const fmt = (v) => {
    const n = parseFloat(v);
    if ((!v && v !== 0) || isNaN(n)) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ── UI Primitives (compact — matches ClientInvoiceCreation) ───────────────────
const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const readonlyCls = `${inputCls} bg-gray-50 dark:bg-gray-700 cursor-default`;

const Label = ({ children, required }) => (
    <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const CardHeader = ({ icon: Icon, title, action }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-indigo-500" />
            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
        </div>
        {action}
    </div>
);

const Card = ({ children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        {children}
    </div>
);

// Sub-section heading inside a card, with optional right-side content
const SubHeading = ({ icon: Icon, children, right }) => (
    <div className="flex items-center justify-between gap-3 mb-2">
        <p className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            <Icon className="h-3.5 w-3.5 text-indigo-500" /> {children}
        </p>
        {right}
    </div>
);

// Small read-only info cell (PO auto-filled details)
const InfoBadge = ({ label, value }) => (
    <div className="px-2.5 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/30 min-w-0">
        <p className="text-[10px] text-indigo-500 dark:text-indigo-400 font-semibold uppercase tracking-wide">{label}</p>
        <p className="text-xs font-bold text-indigo-800 dark:text-indigo-200 truncate" title={value || ''}>{value || '—'}</p>
    </div>
);

// Summary panel row
const SumRow = ({ label, value, className = '' }) => (
    <div className={`flex justify-between gap-3 ${className}`}>
        <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</dt>
        <dd className="font-semibold text-gray-800 dark:text-gray-100 truncate text-right" title={value || ''}>{value || '—'}</dd>
    </div>
);

const YesNoToggle = ({ value, onChange }) => (
    <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
        {['Yes', 'No'].map(opt => (
            <button key={opt} type="button" onClick={() => onChange(opt)}
                className={`px-4 py-1 text-xs font-semibold transition-all ${
                    value === opt
                        ? opt === 'Yes' ? 'bg-green-500 text-white' : 'bg-rose-500 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
            >{opt}</button>
        ))}
    </div>
);

// ── Vendor Combobox ───────────────────────────────────────────────────────────
const VendorCombobox = ({ vendors, loading, value, onChange }) => {
    const [query, setQuery] = useState('');
    const [open,  setOpen]  = useState(false);
    const containerRef      = useRef(null);
    const inputRef          = useRef(null);

    const selected = vendors.find(v => v.Contractorcode === value) || null;

    const filtered = query.trim()
        ? vendors.filter(v =>
              v.ContractorName.toLowerCase().includes(query.toLowerCase()) ||
              v.Contractorcode.toLowerCase().includes(query.toLowerCase())
          )
        : vendors;

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSelect = (vendor) => {
        onChange(vendor.Contractorcode, vendor.ContractorName);
        setQuery('');
        setOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('', '');
        setQuery('');
        setOpen(false);
    };

    const handleInputClick = () => {
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    return (
        <div ref={containerRef} className="relative">
            {/* Input box */}
            <div
                onClick={handleInputClick}
                className={`${inputCls} flex items-center gap-2 cursor-pointer min-h-[38px]`}
            >
                {loading
                    ? <Loader2 className="h-4 w-4 text-indigo-400 animate-spin flex-shrink-0" />
                    : <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />}

                {open ? (
                    <input
                        ref={inputRef}
                        autoFocus
                        className="flex-1 bg-transparent outline-none text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400"
                        placeholder="Type name or code..."
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onClick={e => e.stopPropagation()}
                    />
                ) : (
                    <span className={`flex-1 min-w-0 text-sm truncate ${selected ? 'text-gray-800 dark:text-gray-100 font-medium' : 'text-gray-400'}`}
                        title={selected ? `${selected.Contractorcode} — ${selected.ContractorName}` : ''}>
                        {selected ? (
                            <>
                                <span className="font-mono text-xs font-bold text-indigo-600 dark:text-indigo-400 mr-1.5">{selected.Contractorcode}</span>
                                {selected.ContractorName.replace(/\([^)]*\)$/, '').trim()}
                            </>
                        ) : loading ? 'Loading vendors...' : 'Select service vendor...'}
                    </span>
                )}

                {selected && (
                    <button type="button" onClick={handleClear}
                        className="p-0.5 rounded-full text-gray-400 hover:text-rose-500 transition-colors flex-shrink-0">
                        <X className="h-3.5 w-3.5" />
                    </button>
                )}
                {!selected && <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />}
            </div>


            {/* Dropdown */}
            {open && (
                <div className="absolute z-[200] left-0 right-0 mt-1 bg-white dark:bg-gray-800 border-2 border-indigo-200 dark:border-indigo-700 rounded-xl shadow-2xl max-h-64 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-400">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="py-4 text-sm text-gray-400 text-center">No vendors found</div>
                    ) : (
                        filtered.slice(0, 100).map(v => (
                            <div
                                key={v.Contractorcode}
                                onClick={() => handleSelect(v)}
                                className={`px-4 py-3 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-0 transition-colors
                                    ${value === v.Contractorcode
                                        ? 'bg-indigo-50 dark:bg-indigo-900/30'
                                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                            >
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 leading-tight">
                                    {v.ContractorName.replace(/\([^)]*\)$/, '').trim()}
                                </p>
                                <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-0.5 font-mono">{v.Contractorcode}</p>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

// ── Initial State ─────────────────────────────────────────────────────────────
const INITIAL_FORM = {
    VendorCode: '', VendorDisplayName: '',
    SPPONo: '', CCCode: '', DCACode: '', SubDCACode: '',
    VendorName: '', Balance: 0, Advance: 0,
    CCType: '', CCSubType: '', POStartDate: '',
    SPPOInvoiceNo: '', SPPOInvoiceDate: '', SPPOInvoiceMakingDate: '',
    SPPOBasicValue: '', Description: '',
    IsGstApplicable: 'No',
    // Legacy GSTType radio: 'Creditable' | 'Non-Creditable'
    GSTType: '',
    VendorGST: '', VendorGSTTaxId: '', CompanyGST: '',
    Statecheck: false,
    Cgstsdca: '', Cgstsdcaamt: '',
    Sgstsdca: '', Sgstsdcaamt: '',
    Igstsdca: '', Igstsdcaamt: '',
    Retention: '', Hold: '',
    Filechk: 'No', Extension: '',
};

let _rowId = 0;
const newRow = () => ({ id: ++_rowId, dcaCode: '', sdcaCode: '', amount: '' });

// ── Component ─────────────────────────────────────────────────────────────────
const SPPOInvoiceCreation = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector(s => s.auth);
    const roleId    = userData?.roleId    || userData?.RID || 0;
    const createdBy = userData?.userName  || userData?.username || 'User';

    // Redux selectors
    const serviceVendors         = useSelector(selectSPPOInvServiceVendors);
    const loadingServiceVendors  = useSelector(selectSPPOInvServiceVendorsLoading);
    const poList          = useSelector(selectSPPOInvPOList);
    const poDetails       = useSelector(selectSPPOInvPODetails);
    const vendorGSTNos    = useSelector(selectSPPOInvVendorGSTNos);
    const companyGSTNos   = useSelector(selectSPPOInvCompanyGSTNos);
    const gstConfig       = useSelector(selectSPPOInvGSTConfig);
    const otherDCAs       = useSelector(selectSPPOInvOtherDCAs);
    const deductionDCAs   = useSelector(selectSPPOInvDeductionDCAs);
    const budgetResult    = useSelector(selectSPPOInvBudgetResult);
    const saveResult      = useSelector(selectSPPOInvSaveResult);
    const saveError       = useSelector(selectSPPOInvSaveError);

    const loadingPOList      = useSelector(selectSPPOInvPOListLoading);
    const loadingPODetails   = useSelector(selectSPPOInvPODetailsLoading);
    const loadingVendorGST   = useSelector(selectSPPOInvVendorGSTLoading);
    const loadingCompanyGST  = useSelector(selectSPPOInvCompanyGSTLoading);
    const loadingGSTConfig   = useSelector(selectSPPOInvGSTConfigLoading);
    const loadingOtherDCAs   = useSelector(selectSPPOInvOtherDCAsLoading);
    const loadingDeductDCAs  = useSelector(selectSPPOInvDeductionDCAsLoading);
    const loadingBudget      = useSelector(selectSPPOInvBudgetLoading);
    const loadingSave        = useSelector(selectSPPOInvSaveLoading);

    // Local state
    const [form, setForm]             = useState(INITIAL_FORM);
    const [otherCharges, setOtherCharges] = useState([newRow()]);
    const [deductions,   setDeductions]   = useState([newRow()]);
    // SubDCA cache: { [dcaCode]: SubDCA[] }
    const [subDCACache,      setSubDCACache]      = useState({});
    const [loadingSubDCAs,   setLoadingSubDCAs]   = useState({});
    // Track if budget has been checked
    const [, setBudgetChecked] = useState(false);
    // File upload state
    const [attachedFile,    setAttachedFile]    = useState(null);   // File object
    const [uploadProgress,  setUploadProgress]  = useState(0);      // 0-100
    const [uploadStatus,    setUploadStatus]     = useState('idle'); // 'idle'|'uploading'|'done'|'error'
    const [uploadError,     setUploadError]      = useState('');
    const fileInputRef = useRef(null);

    const isBusy = loadingSave || loadingBudget || uploadStatus === 'uploading';

    // ── SubDCA fetch (local, not redux) ───────────────────────────────────────
    const fetchSubDCAs = async (dcaCode) => {
        if (!dcaCode || subDCACache[dcaCode] !== undefined) return;
        setLoadingSubDCAs(p => ({ ...p, [dcaCode]: true }));
        try {
            const res = await getSubDCAByDCA(dcaCode);
            setSubDCACache(p => ({ ...p, [dcaCode]: res.data?.Data || [] }));
        } catch {
            setSubDCACache(p => ({ ...p, [dcaCode]: [] }));
        } finally {
            setLoadingSubDCAs(p => ({ ...p, [dcaCode]: false }));
        }
    };

    // ── Bootstrap ─────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchServiceVendors());
        dispatch(fetchCompanyGSTNos());
        return () => dispatch(resetSppoInvoice());
    }, [dispatch]);

    // ── PO details → form ──────────────────────────────────────────────────────
    useEffect(() => {
        if (!poDetails) return;
        setForm(f => ({
            ...f,
            CCCode: poDetails.CCCode || '', DCACode: poDetails.DCACode || '',
            SubDCACode: poDetails.SubDCACode || '', VendorName: poDetails.VendorName || '',
            Balance: poDetails.Balance || 0, Advance: poDetails.Advance || 0,
            CCType: poDetails.CCType || '', CCSubType: poDetails.CCSubType || '',
            POStartDate: poDetails.POStartDate || '',
        }));
    }, [poDetails]);

    // ── CCCode + InvDate → DCA lists ──────────────────────────────────────────
    useEffect(() => {
        const invDate = formatDateForAPI(form.SPPOInvoiceDate);
        if (!form.CCCode || !invDate) return;
        dispatch(fetchOtherChargeDCAs({ ccCode: form.CCCode, invDate }));
        dispatch(fetchDeductionDCAs({ ccCode: form.CCCode, invDate }));
    }, [form.CCCode, form.SPPOInvoiceDate, dispatch]);

    // ── GST config auto-fetch ─────────────────────────────────────────────────
    useEffect(() => {
        const { IsGstApplicable, CompanyGST, VendorGST, CCCode, SPPONo, SPPOInvoiceDate } = form;
        if (IsGstApplicable !== 'Yes') return;
        if (!form.GSTType || !CompanyGST || !VendorGST || !CCCode || !SPPONo || !SPPOInvoiceDate) return;
        const invDate = formatDateForAPI(SPPOInvoiceDate);
        dispatch(fetchGSTConfig({
            CompanyGST, VendorClientGSTNo: VendorGST,
            InvoiceType: 'Vendor', Cccode: CCCode, Taxtype: form.GSTType,
            PONO: SPPONo, InvDate: invDate,
            Configtyp: 'Input GST', VendorOrClient: 'Service Provider',
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.IsGstApplicable, form.GSTType, form.CompanyGST, form.VendorGST, form.CCCode, form.SPPONo, form.SPPOInvoiceDate, dispatch]);

    // ── GST config → populate DCA fields ─────────────────────────────────────
    useEffect(() => {
        if (!gstConfig) return;
        setForm(f => ({
            ...f,
            Statecheck: gstConfig.SameState,
            Cgstsdca: gstConfig.CGSTSubDCA || '',
            Sgstsdca: gstConfig.SGSTSubDCA || '',
            Igstsdca: gstConfig.IGSTSubDCA || '',
            Cgstsdcaamt: '', Sgstsdcaamt: '', Igstsdcaamt: '',
        }));
    }, [gstConfig]);

    // ── Save/upload errors (fallback, should be handled inline) ──────────────
    useEffect(() => {
        if (saveError) toast.error(String(saveError));
    }, [saveError]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

    const handleVendorSelect = (code, name) => {
        dispatch(clearPOList()); dispatch(clearPODetails()); dispatch(clearGSTConfig());
        setBudgetChecked(false);
        setForm(f => ({
            ...f, VendorCode: code, VendorDisplayName: name || '',
            SPPONo: '', CCCode: '', DCACode: '', SubDCACode: '',
            VendorName: '', Balance: 0, Advance: 0,
            IsGstApplicable: 'No', VendorGST: '', CompanyGST: '',
            Cgstsdca: '', Sgstsdca: '', Igstsdca: '',
            Cgstsdcaamt: '', Sgstsdcaamt: '', Igstsdcaamt: '', Statecheck: false,
        }));
        if (code) { dispatch(fetchPOsForVendor(code)); dispatch(fetchVendorGSTNos(code)); }
    };

    const handlePOChange = (poNo) => {
        dispatch(clearPODetails()); dispatch(clearGSTConfig());
        setBudgetChecked(false);
        setForm(f => ({
            ...f, SPPONo: poNo, CCCode: '', DCACode: '', SubDCACode: '',
            VendorName: '', Balance: 0, Advance: 0,
            Cgstsdca: '', Sgstsdca: '', Igstsdca: '',
            Cgstsdcaamt: '', Sgstsdcaamt: '', Igstsdcaamt: '', Statecheck: false,
        }));
        if (poNo) dispatch(fetchPODetails(poNo));
    };

    const handleGSTApplicable = (val) => {
        setForm(f => ({
            ...f, IsGstApplicable: val, GSTType: '',
            VendorGST: '', VendorGSTTaxId: '', CompanyGST: '',
            Statecheck: false,
            Cgstsdca: '', Cgstsdcaamt: '', Sgstsdca: '', Sgstsdcaamt: '',
            Igstsdca: '', Igstsdcaamt: '',
        }));
        dispatch(clearGSTConfig());
    };

    // Legacy GSTType change: reload company GSTs for that type, clear the selection.
    const handleGSTTypeChange = (val) => {
        setForm(f => ({ ...f, GSTType: val, CompanyGST: '' }));
        dispatch(clearGSTConfig());
        if (val) dispatch(fetchCompanyGSTNos(val));
    };

    // Other charges rows
    const addOtherRow    = ()         => setOtherCharges(r => [...r, newRow()]);
    const removeOtherRow = (id)       => setOtherCharges(r => r.filter(x => x.id !== id));
    const updateOtherRow = (id, k, v) => {
        setOtherCharges(r => r.map(x => x.id === id ? { ...x, [k]: v } : x));
        if (k === 'dcaCode' && v) fetchSubDCAs(v);
    };

    // Deduction rows
    const addDeductRow    = ()         => setDeductions(r => [...r, newRow()]);
    const removeDeductRow = (id)       => setDeductions(r => r.filter(x => x.id !== id));
    const updateDeductRow = (id, k, v) => {
        setDeductions(r => r.map(x => x.id === id ? { ...x, [k]: v } : x));
        if (k === 'dcaCode' && v) fetchSubDCAs(v);
    };

    // ── File upload ───────────────────────────────────────────────────────────
    const handleFileSelect = (file) => {
        if (!file) return;
        if (file.type !== 'application/pdf') {
            toast.error('Only PDF files are allowed.');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be under 10 MB.');
            return;
        }
        // Just hold the file — actual S3 upload happens after invoice is saved
        setAttachedFile(file);
        setUploadStatus('ready');
        setUploadProgress(0);
        setUploadError('');
    };

    const handleRemoveFile = () => {
        setAttachedFile(null);
        setUploadProgress(0);
        setUploadStatus('idle');
        setUploadError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ── Calculations ──────────────────────────────────────────────────────────
    const basicValue  = parseFloat(form.SPPOBasicValue) || 0;
    const gstTotal    = form.IsGstApplicable !== 'Yes' ? 0
        : form.Statecheck
            ? (parseFloat(form.Cgstsdcaamt) || 0) + (parseFloat(form.Sgstsdcaamt) || 0)
            : (parseFloat(form.Igstsdcaamt) || 0);
    const otherTotal  = otherCharges.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const deductTotal = deductions.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const invoiceValue = basicValue + gstTotal + otherTotal;
    const netAmount    = invoiceValue - deductTotal
        - (parseFloat(form.Retention) || 0)
        - (parseFloat(form.Hold) || 0)
        - (parseFloat(form.Advance) || 0);

    // ── Builders ──────────────────────────────────────────────────────────────
    // Legacy SaveSPPOInvoice: Taxtypes = GST type (Creditable/Non-Creditable), Taxdcas = the Input GST
    // account head (config DCAcode), Taxamounts = GST total (CGST+SGST or IGST) as single values; the
    // sub heads/amounts go separately in Cgstsdca/Sgstsdca/Igstsdca(+amt). spInsertSPPOInvoice debits
    // budget by @Taxtype ('Creditable' -> CCC) and stores TaxType/DcaCode on VendorTaxes from these.
    const buildTaxStrings = () => {
        if (form.IsGstApplicable !== 'Yes' || !gstConfig)
            return { Taxtypes: '', Taxdcas: '', Taxamounts: 0 };
        return {
            Taxtypes: form.GSTType,
            Taxdcas: gstConfig.DCAcode || '',
            Taxamounts: gstTotal,
        };
    };

    const buildChargeStrings = (rows) => {
        const valid = rows.filter(r => r.dcaCode && r.sdcaCode && r.amount);
        return {
            dcas:    valid.length ? valid.map(r => r.dcaCode).map((v) => `${v},`).join('')  : null,
            sdcas:   valid.length ? valid.map(r => r.sdcaCode).map((v) => `${v},`).join('') : null,
            amounts: valid.length ? valid.map(r => r.amount).map((v) => `${v},`).join('')   : null,
        };
    };

    const buildBudgetPayload = () => {
        const taxStr = buildTaxStrings();
        const other  = buildChargeStrings(otherCharges);
        return {
            CCCode:             form.CCCode,
            SPPOInvoiceDate:    formatDateForAPI(form.SPPOInvoiceDate),
            Taxtypes:           taxStr.Taxtypes,
            Taxdcas:            taxStr.Taxdcas,
            Taxamounts:         taxStr.Taxamounts,
            Otherchargedcas:    other.dcas,
            Otherchargeamounts: other.amounts,
        };
    };

    // spCheckSPPOInvoiceBudget returns 'Sufficent' (sic) when the budget is enough.
    const isBudgetOk = (msg) => /^suffic?i?ent$/i.test(String(msg || '').trim());

    // ── Validate form ─────────────────────────────────────────────────────────
    const validateForm = () => {
        if (!form.VendorCode || !form.SPPONo)
            { toast.error('Select vendor and PO number.'); return false; }
        if (!form.SPPOInvoiceNo.trim())
            { toast.error('Invoice number is required.'); return false; }
        if (!form.SPPOInvoiceDate)
            { toast.error('Invoice date is required.'); return false; }
        if (!form.SPPOInvoiceMakingDate)
            { toast.error('Invoice making date is required.'); return false; }
        if (!form.SPPOBasicValue || basicValue <= 0)
            { toast.error('Basic value must be greater than 0.'); return false; }
        if (basicValue > form.Balance)
            { toast.error(`Basic value (₹ ${fmt(basicValue)}) cannot exceed PO balance (₹ ${fmt(form.Balance)}).`); return false; }
        if (form.IsGstApplicable === 'Yes' && !form.GSTType)
            { toast.error('Select GST Type'); return false; }
        if (form.IsGstApplicable === 'Yes' && (!form.VendorGST || !form.CompanyGST))
            { toast.error('Select vendor and company GST numbers.'); return false; }
        // Legacy: the Input GST configuration must have all 4 heads (DCA + CGST/SGST/IGST sub DCAs).
        if (form.IsGstApplicable === 'Yes' && Number(gstConfig?.ConfigCount) !== 4)
            { toast.error('Input GST Data Is Not Configured'); return false; }
        // Legacy CountNetAmount checks.
        if (invoiceValue < (parseFloat(form.Advance) || 0))
            { toast.error('Advance Amount Should Not Be Greater Than Invoice Value'); return false; }
        if (basicValue < deductTotal + (parseFloat(form.Retention) || 0) + (parseFloat(form.Hold) || 0) + (parseFloat(form.Advance) || 0))
            { toast.error('Sum of [Deduction+Retention+Hold+Advance] Should not be Greater than Basic Amount'); return false; }
        if (netAmount <= 0)
            { toast.error('Net Amount must be greater than 0'); return false; }
        // Validate other charges: SubDCA mandatory if row has DCA + amount
        const invalidOther = otherCharges.some(r => (r.dcaCode || r.amount) && !r.sdcaCode);
        if (invalidOther)
            { toast.error('Sub-DCA is mandatory for all Other Charges rows.'); return false; }
        const invalidDeduct = deductions.some(r => (r.dcaCode || r.amount) && !r.sdcaCode);
        if (invalidDeduct)
            { toast.error('Sub-DCA is mandatory for all Deduction rows.'); return false; }
        return true;
    };

    // ── Direct S3 PUT (XMLHttpRequest for progress tracking) ─────────────────
    const uploadToS3 = (file, s3Key, onProgress) =>
        new Promise((resolve, reject) => {
            const url = `${S3_BASE_URL}/${UPLOAD_DOCS_PATH}/${S3_FOLDERS.VENDOR_INVOICES}/${s3Key}`;
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable && onProgress)
                    onProgress(Math.round((e.loaded / e.total) * 100));
            });
            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) resolve();
                else reject(new Error(`S3 upload failed: HTTP ${xhr.status}`));
            });
            xhr.addEventListener('error', () => reject(new Error('Network error during S3 upload')));
            xhr.open('PUT', url);
            xhr.setRequestHeader('Content-Type', 'application/pdf');
            xhr.send(file);
        });

    // ── Submit: Phase 1 save → Phase 2 direct S3 PUT ─────────────────────────
    const handleSubmit = async () => {
        if (!validateForm()) return;

        const buildPayload = () => {
            const taxStr = buildTaxStrings();
            const other  = buildChargeStrings(otherCharges);
            const deduct = buildChargeStrings(deductions);
            return {
                CCCode: form.CCCode, VendorCode: form.VendorCode,
                SPPOInvoiceDate: formatDateForAPI(form.SPPOInvoiceDate),
                SPPOInvoiceMakingDate: formatDateForAPI(form.SPPOInvoiceMakingDate),
                SPPOInvoiceNo: form.SPPOInvoiceNo,
                SPPOBasicValue: basicValue,
                DCACode: form.DCACode, SubDCACode: form.SubDCACode, SPPONo: form.SPPONo,
                ...taxStr,
                Otherchargedcas: other.dcas, Otherchargesdcas: other.sdcas, Otherchargeamounts: other.amounts,
                Deductiondcas: deduct.dcas, Deductionsdcas: deduct.sdcas, Deductionamounts: deduct.amounts,
                InvoiceValue: invoiceValue, Description: form.Description,
                Retention: parseFloat(form.Retention) || 0,
                Hold: parseFloat(form.Hold) || 0,
                NetAmount: netAmount,
                Advance: parseFloat(form.Advance) || 0,
                VendorGST: form.VendorGST, CompanyGST: form.CompanyGST, Statecheck: form.Statecheck,
                Cgstsdca: form.Cgstsdca, Cgstsdcaamt: parseFloat(form.Cgstsdcaamt) || 0,
                Sgstsdca: form.Sgstsdca, Sgstsdcaamt: parseFloat(form.Sgstsdcaamt) || 0,
                Igstsdca: form.Igstsdca, Igstsdcaamt: parseFloat(form.Igstsdcaamt) || 0,
                TaxApplicable: form.IsGstApplicable,
                CreatedBy: String(createdBy), RoleId: Number(roleId),
                // SP checks Filechk='Yes' to generate the S3 key (VendorCode_ID.ext)
                Filechk:   attachedFile ? 'Yes' : 'No',
                Extension: attachedFile ? 'pdf' : '',
            };
        };

        const doSave = async () => {
            // Phase 1: save invoice — SP generates S3 key, returns "Submited,<s3key>"
            const result   = await dispatch(submitSPPOInvoice(buildPayload()));
            const response = String(result?.payload || '');

            if (!response.toLowerCase().startsWith('submited') && !response.toLowerCase().startsWith('submitted')) {
                toast.error(response || 'Failed to save SPPO Invoice.');
                dispatch(clearSaveResult());
                return;
            }

            // Phase 2: PUT file directly to S3 using the key the SP generated
            if (attachedFile) {
                const s3Key = response.split(',')[1]?.trim(); // e.g. "SP100787_123.pdf"
                if (s3Key) {
                    setUploadStatus('uploading');
                    setUploadProgress(0);
                    try {
                        await uploadToS3(attachedFile, s3Key, (pct) => setUploadProgress(pct));
                        toast.success('Invoice submitted and PDF uploaded successfully!');
                    } catch (err) {
                        toast.warning(`Invoice saved (${s3Key}) but PDF upload failed: ${err.message}. You can re-upload later.`);
                        setUploadStatus('error');
                        setUploadError(err.message);
                        dispatch(clearSaveResult());
                        return;
                    }
                } else {
                    toast.success('SPPO Invoice submitted successfully!');
                }
            } else {
                toast.success('SPPO Invoice submitted successfully!');
            }

            dispatch(clearSaveResult());
            handleReset();
        };

        // Legacy checks the budget on every submit and stops unless it is sufficient.
        const budgetRes = await dispatch(checkBudget(buildBudgetPayload()));
        const budgetMsg = budgetRes?.payload;
        if (!isBudgetOk(budgetMsg)) {
            toast.error(String(budgetMsg || 'Budget check failed'));
            setBudgetChecked(false);
            return;
        }
        setBudgetChecked(true);
        await doSave();
    };

    const handleReset = () => {
        setForm(INITIAL_FORM);
        setOtherCharges([newRow()]);
        setDeductions([newRow()]);
        setSubDCACache({});
        setLoadingSubDCAs({});
        setBudgetChecked(false);
        setAttachedFile(null);
        setUploadProgress(0);
        setUploadStatus('idle');
        setUploadError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        dispatch(resetSppoInvoice());
        dispatch(fetchServiceVendors());
        dispatch(fetchCompanyGSTNos());
    };

    // ── Row renderer helpers ──────────────────────────────────────────────────
    const renderChargeRows = (rows, dcaList, loadingDCA, updateFn, removeFn) => (
        <div className="space-y-2">
            {rows.map((row, idx) => {
                const rowSubDCAs = row.dcaCode ? (subDCACache[row.dcaCode] || []) : [];
                const isLoadingSubDCA = row.dcaCode && loadingSubDCAs[row.dcaCode];
                return (
                    <div key={row.id} className="grid grid-cols-12 gap-2 items-end">
                        {/* DCA */}
                        <div className="col-span-12 sm:col-span-5">
                            {idx === 0 && <Label required>DCA</Label>}
                            <div className="relative">
                                <select
                                    className={`${inputCls} appearance-none pr-8`}
                                    value={row.dcaCode}
                                    onChange={e => {
                                        updateFn(row.id, 'dcaCode', e.target.value);
                                        updateFn(row.id, 'sdcaCode', '');
                                    }}
                                    disabled={loadingDCA || isBusy}
                                >
                                    <option value="">{loadingDCA ? 'Loading...' : 'Select DCA...'}</option>
                                    {dcaList.map(d => (
                                        <option key={d.DCAID} value={d.DCACode}>{d.DCAIDSTR}</option>
                                    ))}
                                </select>
                                <SelectIcon loading={loadingDCA} />
                            </div>
                        </div>
                        {/* Sub-DCA */}
                        <div className="col-span-6 sm:col-span-4">
                            {idx === 0 && <Label required>Sub-DCA</Label>}
                            <div className="relative">
                                <select
                                    className={`${inputCls} appearance-none pr-8`}
                                    value={row.sdcaCode}
                                    onChange={e => updateFn(row.id, 'sdcaCode', e.target.value)}
                                    disabled={!row.dcaCode || isLoadingSubDCA || isBusy}
                                >
                                    <option value="">
                                        {!row.dcaCode ? 'Select DCA first' : isLoadingSubDCA ? 'Loading...' : 'Select Sub-DCA...'}
                                    </option>
                                    {rowSubDCAs.map(d => (
                                        <option key={d.SubDCACode} value={d.SubDCACode}>{d.SubDCAIDSTR}</option>
                                    ))}
                                </select>
                                <SelectIcon loading={!!isLoadingSubDCA} />
                            </div>
                        </div>
                        {/* Amount */}
                        <div className="col-span-5 sm:col-span-2">
                            {idx === 0 && <Label required>Amount (₹)</Label>}
                            <input
                                className={inputCls}
                                type="number" min="0" step="0.01" placeholder="0.00"
                                value={row.amount}
                                onChange={e => updateFn(row.id, 'amount', e.target.value)}
                                disabled={isBusy}
                            />
                        </div>
                        {/* Remove */}
                        <div className="col-span-1 flex justify-center pb-1">
                            {rows.length > 1 && (
                                <button type="button" onClick={() => removeFn(row.id)}
                                    disabled={isBusy}
                                    className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors disabled:opacity-40">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const hasBudgetWarning = typeof budgetResult === 'string' && budgetResult.trim();
    const deductionLines = [
        { label: 'Deductions', val: deductTotal },
        { label: 'Retention',  val: parseFloat(form.Retention) || 0 },
        { label: 'Hold',       val: parseFloat(form.Hold) || 0 },
        { label: 'Advance',    val: parseFloat(form.Advance) || 0 },
    ].filter(x => x.val > 0);

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* ── Page Header (slim) ── */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <Receipt className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                            {menuData?.name || 'SPPO Invoice Creation'}
                        </h1>
                        <p className="text-indigo-200 text-xs">Purchase Module · Service Provider Purchase Order Invoice</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

                {/* ── Left: form ───────────────────────────────────────────── */}
                <div className="lg:col-span-2 space-y-4">

                    {/* Vendor, PO, invoice and GST */}
                    <Card>
                        <CardHeader icon={FileText} title="Invoice Details" />
                        <div className="p-4 space-y-4">

                            {/* Vendor & PO */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div className="sm:col-span-2">
                                    <Label required>Service Vendor</Label>
                                    <VendorCombobox
                                        vendors={serviceVendors}
                                        loading={loadingServiceVendors}
                                        value={form.VendorCode}
                                        onChange={handleVendorSelect}
                                    />
                                </div>
                                <div>
                                    <Label required>PO Number</Label>
                                    <div className="relative">
                                        <select
                                            className={`${inputCls} appearance-none pr-8`}
                                            value={form.SPPONo}
                                            onChange={e => handlePOChange(e.target.value)}
                                            disabled={!poList.length || loadingPODetails || !form.VendorCode || isBusy}
                                        >
                                            <option value="">
                                                {!form.VendorCode ? 'Select vendor first' : loadingPOList ? 'Loading...' : 'Select PO...'}
                                            </option>
                                            {poList.map(po => (
                                                <option key={po.SPPONo} value={po.SPPONo}>{po.SPPONoDesc}</option>
                                            ))}
                                        </select>
                                        <SelectIcon loading={loadingPODetails || loadingPOList} />
                                    </div>
                                </div>
                            </div>

                            {/* PO auto-filled details */}
                            {form.CCCode && (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    <InfoBadge label="Vendor"      value={form.VendorName} />
                                    <InfoBadge label="CC Code"     value={form.CCCode} />
                                    <InfoBadge label="DCA / Sub"   value={[form.DCACode, form.SubDCACode].filter(Boolean).join(' / ')} />
                                    <InfoBadge label="PO Balance"  value={`₹ ${fmt(form.Balance)}`} />
                                    <InfoBadge label="CC Type"     value={form.CCType} />
                                    <InfoBadge label="CC Sub-Type" value={form.CCSubType} />
                                    <InfoBadge label="PO Start"    value={form.POStartDate} />
                                    <InfoBadge label="Advance"     value={`₹ ${fmt(form.Advance)}`} />
                                </div>
                            )}

                            {/* Invoice */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                                <div>
                                    <Label required>Invoice Number</Label>
                                    <input className={inputCls} placeholder="Vendor's invoice number"
                                        value={form.SPPOInvoiceNo}
                                        onChange={e => setField('SPPOInvoiceNo', e.target.value)}
                                        disabled={isBusy} />
                                </div>
                                <div>
                                    <Label required>Invoice Date</Label>
                                    <CustomDatePicker value={form.SPPOInvoiceDate}
                                        onChange={val => setField('SPPOInvoiceDate', val)}
                                        format="DD-MMM-YYYY" placeholder="Invoice date" />
                                </div>
                                <div>
                                    <Label required>Invoice Making Date</Label>
                                    <CustomDatePicker value={form.SPPOInvoiceMakingDate}
                                        onChange={val => setField('SPPOInvoiceMakingDate', val)}
                                        format="DD-MMM-YYYY" placeholder="Making date" />
                                </div>
                                <div>
                                    <Label required>Basic Value (₹)</Label>
                                    <div className="relative">
                                        <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                                        <input className={`${inputCls} pl-8 ${form.Balance > 0 && basicValue > form.Balance ? 'border-rose-400' : ''}`}
                                            type="number" min="0" step="0.01" placeholder="0.00"
                                            value={form.SPPOBasicValue}
                                            onChange={e => { setField('SPPOBasicValue', e.target.value); setBudgetChecked(false); }}
                                            disabled={isBusy} />
                                    </div>
                                    {form.Balance > 0 && (
                                        <p className={`text-[11px] mt-1 ${basicValue > form.Balance ? 'text-rose-500' : 'text-indigo-500 dark:text-indigo-400'}`}>
                                            Max: ₹ {fmt(form.Balance)}
                                        </p>
                                    )}
                                </div>
                                <div className="sm:col-span-2">
                                    <Label>Description</Label>
                                    <input className={inputCls} placeholder="Invoice description / remarks"
                                        value={form.Description}
                                        onChange={e => setField('Description', e.target.value)}
                                        disabled={isBusy} />
                                </div>
                            </div>

                            {/* GST */}
                            <div className="pt-4 border-t border-gray-100 dark:border-gray-700 space-y-3">
                                <SubHeading icon={ShieldCheck} right={<YesNoToggle value={form.IsGstApplicable} onChange={handleGSTApplicable} />}>
                                    GST Applicable?<span className="text-rose-500">*</span>
                                </SubHeading>

                                {form.IsGstApplicable === 'Yes' && (
                                    <>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <Label required>GST Type</Label>
                                                <div className="relative">
                                                    <select className={`${inputCls} appearance-none pr-8`}
                                                        value={form.GSTType}
                                                        onChange={e => handleGSTTypeChange(e.target.value)}
                                                        disabled={isBusy}>
                                                        <option value="">Select GST type...</option>
                                                        <option value="Creditable">Creditable</option>
                                                        <option value="Non-Creditable">Non-Creditable</option>
                                                    </select>
                                                    <SelectIcon loading={false} />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <Label required>Vendor GST</Label>
                                                <div className="relative">
                                                    <select className={`${inputCls} appearance-none pr-8`}
                                                        value={form.VendorGST}
                                                        onChange={e => {
                                                            const sel = vendorGSTNos.find(g => g.TaxNoName === e.target.value);
                                                            setForm(f => ({ ...f, VendorGST: e.target.value, VendorGSTTaxId: sel?.TaxNoID || '' }));
                                                            dispatch(clearGSTConfig());
                                                        }}
                                                        disabled={loadingVendorGST || !vendorGSTNos.length || isBusy}
                                                    >
                                                        <option value="">Select vendor GST...</option>
                                                        {vendorGSTNos.map(g => (
                                                            <option key={g.TaxNoID} value={g.TaxNoName}>{g.TaxNoName}</option>
                                                        ))}
                                                    </select>
                                                    <SelectIcon loading={loadingVendorGST} />
                                                </div>
                                                {!loadingVendorGST && !vendorGSTNos.length && form.VendorCode && (
                                                    <p className="text-[11px] text-amber-500 mt-1">No GST numbers found for this vendor.</p>
                                                )}
                                            </div>
                                            <div>
                                                <Label required>Company GST</Label>
                                                <div className="relative">
                                                    <select className={`${inputCls} appearance-none pr-8`}
                                                        value={form.CompanyGST}
                                                        onChange={e => { setForm(f => ({ ...f, CompanyGST: e.target.value })); dispatch(clearGSTConfig()); }}
                                                        disabled={!form.GSTType || loadingCompanyGST || !companyGSTNos.length || isBusy}
                                                    >
                                                        <option value="">Select company GST...</option>
                                                        {companyGSTNos.map(g => (
                                                            <option key={g.TaxNoID} value={g.GSTNo}>{g.TaxNoName}</option>
                                                        ))}
                                                    </select>
                                                    <SelectIcon loading={loadingCompanyGST} />
                                                </div>
                                            </div>
                                        </div>

                                        {loadingGSTConfig && (
                                            <div className="flex items-center gap-2 text-xs text-indigo-500">
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Fetching GST configuration...
                                            </div>
                                        )}

                                        {gstConfig && (
                                            <>
                                                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                                                    form.Statecheck
                                                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700'
                                                        : 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700'
                                                }`}>
                                                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                                                    {form.Statecheck ? 'Same State — CGST + SGST applicable' : 'Different State — IGST applicable'}
                                                </div>

                                                {form.Statecheck ? (
                                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                                        <div>
                                                            <Label>CGST Sub-DCA</Label>
                                                            <input className={readonlyCls} value={form.Cgstsdca} readOnly />
                                                        </div>
                                                        <div>
                                                            <Label required>CGST (₹)</Label>
                                                            <input className={inputCls} type="number" min="0" step="0.01" placeholder="0.00"
                                                                value={form.Cgstsdcaamt}
                                                                onChange={e => setField('Cgstsdcaamt', e.target.value)} disabled={isBusy} />
                                                        </div>
                                                        <div>
                                                            <Label>SGST Sub-DCA</Label>
                                                            <input className={readonlyCls} value={form.Sgstsdca} readOnly />
                                                        </div>
                                                        <div>
                                                            <Label required>SGST (₹)</Label>
                                                            <input className={inputCls} type="number" min="0" step="0.01" placeholder="0.00"
                                                                value={form.Sgstsdcaamt}
                                                                onChange={e => setField('Sgstsdcaamt', e.target.value)} disabled={isBusy} />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                                        <div>
                                                            <Label>IGST Sub-DCA</Label>
                                                            <input className={readonlyCls} value={form.Igstsdca} readOnly />
                                                        </div>
                                                        <div>
                                                            <Label required>IGST (₹)</Label>
                                                            <input className={inputCls} type="number" min="0" step="0.01" placeholder="0.00"
                                                                value={form.Igstsdcaamt}
                                                                onChange={e => setField('Igstsdcaamt', e.target.value)} disabled={isBusy} />
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </Card>

                    {/* Other charges & deductions */}
                    <Card>
                        <CardHeader icon={Layers} title="Other Charges & Deductions" />
                        <div className="p-4 space-y-4">
                            <div>
                                <SubHeading icon={Plus} right={
                                    <div className="flex items-center gap-3">
                                        {otherTotal > 0 && <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">₹ {fmt(otherTotal)}</span>}
                                        <button type="button" onClick={addOtherRow} disabled={isBusy}
                                            className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 font-semibold disabled:opacity-40">
                                            <Plus className="h-3.5 w-3.5" /> Add Row
                                        </button>
                                    </div>
                                }>
                                    Other Charges <span className="font-normal normal-case text-gray-400">(optional)</span>
                                </SubHeading>
                                {renderChargeRows(otherCharges, otherDCAs, loadingOtherDCAs, updateOtherRow, removeOtherRow)}
                            </div>

                            <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                                <SubHeading icon={Layers} right={
                                    <div className="flex items-center gap-3">
                                        {deductTotal > 0 && <span className="text-xs font-bold text-rose-700 dark:text-rose-300">₹ {fmt(deductTotal)}</span>}
                                        <button type="button" onClick={addDeductRow} disabled={isBusy}
                                            className="flex items-center gap-1 text-xs text-rose-600 dark:text-rose-400 hover:text-rose-700 font-semibold disabled:opacity-40">
                                            <Plus className="h-3.5 w-3.5" /> Add Row
                                        </button>
                                    </div>
                                }>
                                    Deductions <span className="font-normal normal-case text-gray-400">(optional)</span>
                                </SubHeading>
                                {renderChargeRows(deductions, deductionDCAs, loadingDeductDCAs, updateDeductRow, removeDeductRow)}
                            </div>
                        </div>
                    </Card>

                    {/* Retention / hold + PDF attachment */}
                    <Card>
                        <CardHeader icon={Paperclip} title="Retention, Hold & Attachment" />
                        <div className="p-4 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                    <Label>Retention (₹)</Label>
                                    <input className={inputCls} type="number" min="0" step="0.01" placeholder="0.00"
                                        value={form.Retention} onChange={e => setField('Retention', e.target.value)} disabled={isBusy} />
                                </div>
                                <div>
                                    <Label>Hold (₹)</Label>
                                    <input className={inputCls} type="number" min="0" step="0.01" placeholder="0.00"
                                        value={form.Hold} onChange={e => setField('Hold', e.target.value)} disabled={isBusy} />
                                </div>
                                <div>
                                    <Label>Advance (₹)</Label>
                                    <input className={readonlyCls} value={fmt(form.Advance)} readOnly title="Auto-filled from PO" />
                                </div>
                            </div>

                            <div>
                                <Label>Invoice PDF</Label>

                                {/* Idle — compact file selector */}
                                {uploadStatus === 'idle' && (
                                    <label className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg border-2 border-dashed border-indigo-300 dark:border-indigo-700 bg-indigo-50/40 dark:bg-indigo-900/10 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:border-indigo-400 cursor-pointer transition-all">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="application/pdf"
                                            className="hidden"
                                            onChange={e => handleFileSelect(e.target.files?.[0])}
                                        />
                                        <CloudUpload className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                                        <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">Click to attach invoice PDF</span>
                                        <span className="text-xs text-gray-400 ml-auto">PDF only · Max 10 MB</span>
                                    </label>
                                )}

                                {/* Ready — file selected, uploads after save */}
                                {uploadStatus === 'ready' && attachedFile && (
                                    <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-indigo-200 dark:border-indigo-700 bg-indigo-50/60 dark:bg-indigo-900/10">
                                        <Paperclip className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{attachedFile.name}</p>
                                            <p className="text-[11px] text-indigo-500 dark:text-indigo-400">
                                                {(attachedFile.size / 1024).toFixed(0)} KB · Uploads to S3 after the invoice is saved
                                            </p>
                                        </div>
                                        <button type="button" onClick={handleRemoveFile} disabled={isBusy}
                                            className="p-1 rounded-md text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors flex-shrink-0"
                                            title="Remove attachment">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                )}

                                {/* Uploading — S3 upload in progress after save */}
                                {uploadStatus === 'uploading' && attachedFile && (
                                    <div className="px-3 py-2 rounded-lg border border-indigo-200 dark:border-indigo-700 bg-indigo-50/60 dark:bg-indigo-900/10 space-y-2">
                                        <div className="flex items-center gap-3">
                                            <Upload className="h-4 w-4 text-indigo-500 animate-bounce flex-shrink-0" />
                                            <p className="flex-1 min-w-0 text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{attachedFile.name}</p>
                                            <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400 flex-shrink-0">{uploadProgress}%</span>
                                        </div>
                                        <div className="w-full bg-indigo-100 dark:bg-indigo-900/40 rounded-full h-2 overflow-hidden">
                                            <div
                                                className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300 ease-out"
                                                style={{ width: `${uploadProgress}%` }}
                                            />
                                        </div>
                                        <p className="text-[11px] text-indigo-500 dark:text-indigo-400 font-medium">
                                            Uploading PDF to S3... please do not close or refresh the page.
                                        </p>
                                    </div>
                                )}

                                {/* Error */}
                                {uploadStatus === 'error' && (
                                    <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-rose-200 dark:border-rose-700 bg-rose-50/60 dark:bg-rose-900/10">
                                        <AlertCircle className="h-4 w-4 text-rose-500 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">Upload Failed</p>
                                            <p className="text-[11px] text-rose-500 dark:text-rose-400 truncate">{uploadError}</p>
                                        </div>
                                        <button type="button" onClick={handleRemoveFile}
                                            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-900/30 hover:bg-rose-200 transition-colors flex-shrink-0">
                                            <RotateCcw className="h-3.5 w-3.5" /> Retry
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>

                {/* ── Right: sticky summary + actions ──────────────────────── */}
                <div className="lg:sticky lg:top-4">
                    <Card>
                        <CardHeader icon={Calculator} title="Invoice Summary" />
                        <div className="p-4 space-y-3">
                            <dl className="space-y-1.5 text-xs">
                                <SumRow label="Vendor"      value={form.VendorDisplayName ? form.VendorDisplayName.replace(/\([^)]*\)$/, '').trim() : ''} />
                                <SumRow label="PO Number"   value={form.SPPONo} />
                                <SumRow label="PO Balance"  value={form.SPPONo ? `₹ ${fmt(form.Balance)}` : ''} />
                                <SumRow label="Invoice No." value={form.SPPOInvoiceNo} />
                                <SumRow label="Invoice Date" value={formatDateForAPI(form.SPPOInvoiceDate)} />
                            </dl>

                            <dl className="space-y-1.5 text-xs pt-3 border-t border-gray-100 dark:border-gray-700">
                                <SumRow label="Basic Value"   value={`₹ ${fmt(basicValue)}`} />
                                <SumRow label="GST Total"     value={`₹ ${fmt(gstTotal)}`} />
                                <SumRow label="Other Charges" value={`₹ ${fmt(otherTotal)}`} />
                                <SumRow label="Invoice Total" value={`₹ ${fmt(invoiceValue)}`} className="pt-1.5 border-t border-gray-100 dark:border-gray-700" />
                                {deductionLines.map(({ label, val }) => (
                                    <div key={label} className="flex justify-between gap-3 text-rose-600 dark:text-rose-400">
                                        <dt>— {label}</dt>
                                        <dd className="font-semibold">₹ {fmt(val)}</dd>
                                    </div>
                                ))}
                            </dl>

                            <div className={`px-4 py-3 rounded-lg text-white bg-gradient-to-r ${netAmount < 0 ? 'from-rose-500 to-rose-600' : 'from-indigo-500 to-violet-600'}`}>
                                <p className="text-white/80 text-[10px] font-semibold uppercase tracking-wider">Net Amount</p>
                                <p className="text-2xl font-black">₹ {fmt(netAmount)}</p>
                            </div>

                            {/* Budget result */}
                            {budgetResult !== null && (
                                <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs ${
                                    hasBudgetWarning
                                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200'
                                        : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-800 dark:text-green-200'
                                }`}>
                                    {hasBudgetWarning
                                        ? <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                        : <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />}
                                    <div className="min-w-0">
                                        <p className="font-semibold">{hasBudgetWarning ? 'Budget Warning' : 'Budget Check Passed'}</p>
                                        <p className="break-words">{hasBudgetWarning ? budgetResult : 'Sufficient budget is available for this invoice.'}</p>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-2">
                                <button type="button" onClick={handleReset} disabled={isBusy}
                                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                                <button type="button" onClick={handleSubmit} disabled={isBusy}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-60 text-white text-sm font-bold transition-all shadow-md">
                                    {isBusy
                                        ? <Loader2 className="h-4 w-4 animate-spin" />
                                        : <Send className="h-4 w-4" />}
                                    {uploadStatus === 'uploading'
                                        ? `Uploading PDF ${uploadProgress}%...`
                                        : loadingBudget ? 'Checking Budget...'
                                        : loadingSave   ? 'Saving Invoice...'
                                        : 'Submit Invoice'}
                                </button>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default SPPOInvoiceCreation;
